import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import OnboardingApplicantDetailsForm from '../components/OnboardingApplicantDetailsForm';
import OnboardingHeader from '../components/OnboardingHeader';
import EntityDetailsForm from '../../businessProfiles/components/EntityDetailsForm';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVerifyOnboardingHandler } from '../dataHandlers/verifyOnboardingHandler';
import { useRegisterOnboardingHandler } from '../dataHandlers/registerOnboardingHandler';
import { getFromLocalStorage, removeFromLocalStorage } from '../../../utils/localStorage';
import ScreenLoader from '../../../components/ScreenLoader';
import OnboardingComplete from '../components/OnboardingComplete';
import OnboardingError from '../components/OnboardingError';
import OnboardingReverify from '../components/OnboardingReverify';
import OnboardingDelink from '../components/OnboardingDelink';

const ONBOARDING_TOKEN_KEY = 'onboarding_token';
const ONBOARDING_TIN_KEY = 'onboarding_tin';

const cleartokenAndTinFromLocalStorage = () => {
  removeFromLocalStorage(ONBOARDING_TOKEN_KEY);
  removeFromLocalStorage(ONBOARDING_TIN_KEY);
};

const OnboardingPage = () => {
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedProfileData, setVerifiedProfileData] = useState(null);
  const [applicantDetailsFormData, setApplicantDetailsFormData] = useState(null);
  const [isProcessCompleted, setIsProcessCompleted] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [errorOccurred, setErrorOccurred] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = getFromLocalStorage(ONBOARDING_TOKEN_KEY) ?? null;
  const tinNumber = getFromLocalStorage(ONBOARDING_TIN_KEY) ?? null;

  // Save to localStorage and clear URL params
  useEffect(() => {
    setPageLoading(true);
    const urlToken = searchParams.get('token');
    const urlTinNumber = searchParams.get('tin');

    if (urlToken || urlTinNumber) {
      cleartokenAndTinFromLocalStorage();
      // Save to localStorage
      if (urlToken) localStorage.setItem(ONBOARDING_TOKEN_KEY, urlToken);
      if (urlTinNumber) localStorage.setItem(ONBOARDING_TIN_KEY, urlTinNumber);

      // Clear query params from URL and replace history
      navigate('/onboard', { replace: true });
    }
    setPageLoading(false);
  }, [searchParams, navigate]);

  const {
    onSubmit: onVerify,
    isPending: isVerifyLoading,
    errorMessage,
  } = useVerifyOnboardingHandler({
    onSuccess: (data) => {
      setVerifiedProfileData(data);
      setIsVerified(true);
    },
    onError: () => {
      setIsVerified(false);
      setVerifiedProfileData(null);
      setErrorOccurred(true);
    },
    token: getFromLocalStorage(ONBOARDING_TOKEN_KEY),
  });

  const {
    onSubmit: onRegister,
    isPending: isRegisterLoading,
    error: updateError,
  } = useRegisterOnboardingHandler({
    onSuccess: () => {
      setIsProcessCompleted(true);
      cleartokenAndTinFromLocalStorage();
    },
    onError: () => {
      setIsProcessCompleted(false);
      setErrorOccurred(true);
    },
    token: getFromLocalStorage(ONBOARDING_TOKEN_KEY),
  });

  const handleOnVerify = async (formData) => {
    setApplicantDetailsFormData(formData);
    const { action, ...payload } = formData;
    onVerify(payload);
  };

  const handleOnRegister = async (formData) => {
    let reason;
    switch (applicantDetailsFormData?.action) {
      case 1:
        reason = 'Onboard';
        break;
      case 2:
        reason = 'Reverify';
        break;
      case 3:
        reason = 'Delink';
        break;
      default:
        reason = 'Onboard';
    }
    onRegister({
      ...formData,
      TIN: verifiedProfileData?.TIN,
      action: applicantDetailsFormData?.action ?? 1,
      reason: reason,
    });
  };
  const handleCancel = async () => {};
  const action = applicantDetailsFormData?.action;

  return (
    <>
      <OnboardingHeader />
      {pageLoading ? (
        <ScreenLoader />
      ) : (
        <>
          {isProcessCompleted ? (
            <>
              {action === 1 && <OnboardingComplete verifiedProfileData={verifiedProfileData} />}
              {action === 2 && <OnboardingReverify verifiedProfileData={verifiedProfileData} />}
              {action === 3 && <OnboardingDelink verifiedProfileData={verifiedProfileData} />}
            </>
          ) : (
            <Box sx={{ pt: 8, px: 2 }}>
              {!errorOccurred ? (
                <>
                  <OnboardingApplicantDetailsForm
                    onSubmit={handleOnVerify}
                    isLoading={isVerifyLoading}
                    isVerified={isVerified}
                    tinNumber={tinNumber || null}
                  />
                  {!!verifiedProfileData && (
                    <EntityDetailsForm
                      onSubmit={handleOnRegister}
                      onCancel={handleCancel}
                      initialData={verifiedProfileData}
                      isLoading={isRegisterLoading}
                      apiErrors={updateError}
                    />
                  )}
                </>
              ) : (
                <OnboardingError errorMessage={errorMessage} />
              )}
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default OnboardingPage;
